import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  Divider,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { 
  Assignment, 
  CheckCircle, 
  Cancel,
  CurrencyRupee,
  InsertDriveFile,
  Description as ApplicationIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { formatDuration, format } from 'date-fns';
import { useAuth } from '../../hooks/useAuth';
// import { applicationService } from '../../services/applicationService';
import { useApplicationStats } from '../../hooks/useApplications';

const StatCard = ({ title, value, icon, color, onClick, loading }) => (
  <Card 
    sx={{ 
          height: '100%', 
          cursor: onClick ? 'pointer' : 'default',
          transition: 'transform 0.2s, border-color 0.2s',
          '&:hover': {
            transform: onClick ? 'scale(1.05)' : 'none',
            // boxShadow: onClick ? 3 : 'none',
            borderColor: onClick ? color : 'transparent',
          },
          border: "2px solid",
          borderColor: "transparent",
        }}
    onClick={onClick}
  >
    <CardContent sx={{ mx: 1}}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography color="textSecondary" sx={{ fontSize: 10, fontWeight: 'bold'}} gutterBottom variant="overline">
          {title} 
        </Typography>
      </Box>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box sx={{ color: color }}>
          {icon}
        </Box>
        <Box>
          <Typography variant='h5' component="div" color={color}>
            {loading ? <CircularProgress size={20} /> : value}
          </Typography>
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const OfficerDashboard = () => {
  const navigate = useNavigate();

  const [overviewFY, setOverviewFY] = useState('');
  const { hasAnyRole, userRoleFullName, userDistrict, userSubdivision, userBlock } = useAuth();

  const currentYear = new Date().getFullYear();
  const currentFY = `${currentYear}-${currentYear + 1}`;
  const firstYear = 2024;
  const financialYears = [];
  
  useEffect(() => {
    setOverviewFY(currentFY);
  }, [currentYear]);

  for (let year = currentYear; year >= firstYear; year--) {
    financialYears.push(`${year}-${year + 1}`);
  }

  const [timeRange, setTimeRange] = useState('30'); // days

  // Fetch dashboard statistics
  // const { data: stats, isLoading: statsLoading, refetch: refetchStats } = useQuery({
  //   queryKey: ['dashboard-stats', overviewFY],
  //   queryFn: () => applicationService.getApplicationStats({ finYear: overviewFY }),
  //   refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  // });
  let applicationStatsQuery;
  applicationStatsQuery = useApplicationStats({ finYear: overviewFY });

  const { data: applicationStats, isLoading: statsLoading, refetch } = applicationStatsQuery;

  const roles_report_assigned = ['ahvs_h', 'agri_h', 'horti_h', 'reve_o'];

  return (
    <Box>
      <Grid container mt={1} mb={2}>
        <Grid size={{ xs: 12, md: 6 }} display="flex" alignItems="center">
          <Typography variant="h5" fontWeight="bold">
            Dashboard
          </Typography>

          <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
          
          <Typography sx={{ fontSize: 14, fontWeight: 'bold', color: 'primary.main' }} mr={2}>
            Overview
          </Typography>

          <Select
            id='overview-fy-select'
            value={overviewFY}
            onChange={(e) => setOverviewFY(e.target.value)}
            size='small'
            sx={{
              '& .MuiSelect-select': {
                paddingY: 0.5,
                paddingX: 1.5,
                fontSize: 14,
              }
            }}
          >
            <MenuItem value="all">All Years</MenuItem>
            {financialYears.map((fy) => (
              <MenuItem key={fy} value={fy}>{"FY " + fy}</MenuItem>
            ))}
          </Select>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }} display="flex" alignItems="center" justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}>
          <Typography sx={{ fontSize: 14, fontWeight: 'bold', color: 'primary.main' }} mr={2}>
            {userRoleFullName ? userRoleFullName.toUpperCase() : '-'}
          </Typography>
        </Grid>

        <Grid size={{ xs: 12, md: 12 }} display="flex" alignItems="center" justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}>
            <Typography sx={{ fontSize: 14, fontWeight: 'bold', color: 'primary.main' }} mr={2}>
            (
              {userDistrict 
                  ? `DISTRICT - ${userDistrict.toUpperCase()}` 
                  : '-'
              } 
              {userSubdivision 
                ? ` | SUBDIV - ${userSubdivision.toUpperCase()}` 
                : ''
              } 
              {userBlock 
                ? ` | BLOCK - ${userBlock.toUpperCase()}` 
                : ''
              }
            )
            </Typography>
        </Grid>
        
      </Grid>

      <Divider sx={{ mt: 1, mb: 1 }} />

      {/* Officer Statistics */}
      <Grid container spacing={2} mb={4}>
      {(!hasAnyRole(roles_report_assigned))
        ? (
          <>
            <Grid size={{ xs: 12, sm: 6, md: 3, lg: 2 }}>
              <StatCard
                title="Assigned To You"
                value={applicationStats?.stats?.currently_assigned_applications_count || 0}
                icon={<Assignment />}
                color="secondary.main"
                loading={statsLoading}
                onClick={() => navigate('/applications/assigned')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3, lg: 2 }}>
              <StatCard
                title="New Applications"
                value={applicationStats?.stats?.new_applications_count || 0}
                icon={<InsertDriveFile />}
                color="warning.main"
                loading={statsLoading}
                onClick={() => navigate('/applications/newapplications')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3, lg: 2 }}>
              <StatCard
                title="Total Disbursed"
                value={applicationStats?.stats?.completed_applications_count || 0}
                icon={<CheckCircle />}
                color="success.main"
                loading={statsLoading}
                onClick={() => navigate('/applications?status=COMPLETED')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3, lg: 2}}>
              <StatCard
                title="Total Rejected"
                value={applicationStats?.stats?.rejected_applications_count || 0}
                icon={<Cancel />}
                color="error.main"
                loading={statsLoading}
                onClick={() => navigate('/applications?status=REJECTED')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3, lg: 2 }}>
              <StatCard
                title="Total Applications"
                value={applicationStats?.stats?.total_applications || 0}
                icon={<ApplicationIcon />}
                color="primary.main"
                loading={statsLoading}
                onClick={() => navigate('/applications')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3, lg: 2 }}>
              <StatCard
                title="Amt. Disbursed"
                value={(applicationStats?.stats?.total_amount_disbursed || 0).toLocaleString('en-IN')}
                icon={<CurrencyRupee />}
                color="success.main"
                loading={statsLoading}
              />
            </Grid>
          </>
          ) : (
          <>
            <Grid size={{ xs: 12 }} >
              <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
                Officer's Current Overview
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3, lg: 2 }}>
              <StatCard
                title="New Requests"
                value={applicationStats?.stats?.user_new_reports_count || 0}
                icon={<Assignment />}
                color="secondary.main"
                loading={statsLoading}
                onClick={() => navigate('/inspections/new-requests')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3, lg: 2 }}>
              <StatCard
                title="Unverified Reports"
                value={applicationStats?.stats?.user_unverified_reports_count || 0}
                icon={<InsertDriveFile />}
                color="warning.main"
                loading={statsLoading}
                onClick={() => navigate('/inspections/unverified-reports')}
              />
            </Grid>

            <Grid size={{ xs: 12 }} sx={{ borderTop: '1px solid #e0e0e0', pt: 2 }}>
              <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
                Department/Section 's Overview (FY)
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3, lg: 2 }}>
              <StatCard
                title="New Requests"
                value={applicationStats?.stats?.role_total_new_reports_count_fy || 0}
                icon={<Assignment />}
                color="secondary.main"
                loading={statsLoading}
                onClick={() => navigate('/inspections/new-requests')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3, lg: 2 }}>
              <StatCard
                title="Unverified Reports"
                value={applicationStats?.stats?.role_total_unverified_reports_count_fy || 0}
                icon={<InsertDriveFile />}
                color="warning.main"
                loading={statsLoading}
                onClick={() => navigate('/inspections/unverified-reports')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3, lg: 2 }}>
              <StatCard
                title="Verified Reports"
                value={applicationStats?.stats?.role_total_verified_reports_count_fy || 0}
                icon={<CheckCircle />}
                color="success.main"
                loading={statsLoading}
                onClick={() => navigate('/inspections/verified-reports')}
              />
            </Grid>
            {/* TODO: Changes after adding Status feature on FieldInspectionReport */}
            <Grid size={{ xs: 12, sm: 6, md: 3, lg: 2}}>
              <StatCard
                title="Rejected Reports"
                value={applicationStats?.stats?.rejected_applications_count || 0}
                icon={<Cancel />}
                color="error.main"
                loading={statsLoading}
                onClick={() => navigate('/inspections/rejected-reports')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3, lg: 2 }}>
              <StatCard
                title="Total Reports"
                value={applicationStats?.stats?.role_total_reports_fy || 0}
                icon={<ApplicationIcon />}
                color="primary.main"
                loading={statsLoading}
                onClick={() => navigate('/inspections/all-assigned-requests/head')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3, lg: 2 }}>
              <StatCard
                title="Amt. Disbursed"
                value={(applicationStats?.stats?.total_amount_disbursed || 0).toLocaleString('en-IN')}
                icon={<CurrencyRupee />}
                color="success.main"
                loading={statsLoading}
              />
            </Grid>

          </>
          )
      }
      </Grid>

      {/* Officer Actions */}
      <Grid container spacing={2}>
        {(!hasAnyRole(roles_report_assigned))
          ? (
            <>
            <Grid size={{ xs: 12, md: 8 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
                    Applications Recently Assigned to You
                  </Typography>
                  {
                    (applicationStats?.stats?.currently_assigned_applications_count || 0) > 3 && (
                      <Typography variant="body2" color="text.secondary" mb={2}>
                        {'Showing latest '}
                          {
                            (applicationStats?.stats?.currently_assigned_applications_count > 10)
                              ? '10' 
                              : applicationStats?.stats?.currently_assigned_applications_count
                          }
                        {' of '}
                        {applicationStats?.stats?.currently_assigned_applications_count || 0 }
                      </Typography>
                    )
                  }

                  <Box display="flex" flexDirection="column" gap={1}>
                    {
                      applicationStats?.stats?.currently_assigned_applications?.length > 0 ? (
                        applicationStats.stats.currently_assigned_applications.map((application) => (
                          <Card key={application.id} variant="outlined">
                            <CardContent>
                              <Grid container spacing={1}>
                                <Grid size={{ xs: 12, sm: 4 }}>
                                  <Box display="flex" flexDirection="column" justifyContent="flex-start" height="100%">
                                    <Typography variant="h5">
                                      {application.application_refno}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {application.applicant_name.toUpperCase()}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                  <Box display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
                                    <Typography variant="caption" sx={{ backgroundColor: 'beige', px: 1, color: 'primary', fontWeight: 'bold' }} >
                                      Assigned {formatDuration({ days: Math.floor((new Date() - new Date(application.updated_at)) / (1000 * 60 * 60 * 24)) })} ago • {format(new Date(application.updated_at), 'dd/MM/yyyy')}
                                    </Typography>
                                    <Typography variant="caption" sx={{ px: 1, color: 'text.secondary'}}>
                                      Applied on {format(new Date(application.created_at), 'dd/MM/yyyy')} • {application.damage_type.join(', ').toUpperCase()} Damage
                                    </Typography>
                                    <Divider sx={{ my: 0.5 }} />
                                    <Typography variant="caption" sx={{ px: 1, color: 'text.primary' }}>
                                      {(application.ward_name + ' • ' + application.gpu_name + ' • ' + application.subdivision_name).toUpperCase()}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 2 }} display="flex" alignItems="center" justifyContent="flex-end">
                                  <Button variant="outlined" fullWidth onClick={() => navigate(`/applications/details/${application.application_refno}`)}>
                                    View
                                  </Button>
                                </Grid>
                              </Grid>
                            </CardContent>
                          </Card>
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No applications currently assigned to you.
                        </Typography>
                      )
                    }
                  </Box>
                  
                  <Button 
                    variant="contained" 
                    fullWidth
                    onClick={
                      () => navigate('/applications/assigned')
                    }
                    sx={{ mt: 2 }}
                  >
                    View All Applications Assigned to You
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid size={{ xs: 12, md: 4 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary" fontWeight="bold" mb={2}>
                    Quick Actions
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Button 
                      variant="outlined" 
                      fullWidth
                      onClick={() => navigate('/applications')}
                    >
                      All Applications
                    </Button>
                    <Button 
                      variant="outlined"
                      color="error"
                      fullWidth
                      onClick={() => navigate('/applications?status=REJECTED')}
                    >
                      Rejected Applications
                    </Button>
                    <Button 
                      variant="outlined"
                      fullWidth
                      onClick={() => navigate('/reports')}
                    >
                      View Reports
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            </>
          ) : (
            <>
            <Grid size={{ xs: 12, md: 8 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
                    Recently conducted Field Inspections (Pending Verification by Field Head)
                  </Typography>
                  {
                    (applicationStats?.stats?.user_unverified_reports_count || 0) > 0 && (
                      <Typography variant="body2" color="text.secondary" mb={2}>
                        {'Showing latest '}
                          {
                            (applicationStats?.stats?.user_unverified_reports_count > 10)
                              ? '10' 
                              : applicationStats?.stats?.user_unverified_reports_count
                          }
                        {' of '}
                        {applicationStats?.stats?.user_unverified_reports_count || 0 }
                      </Typography>
                    )
                  }

                  <Box display="flex" flexDirection="column" gap={1}>
                    {
                      applicationStats?.stats?.unverified_reports?.length > 0 ? (
                        applicationStats.stats.unverified_reports.map((application) => (
                          <Card key={application.id} variant="outlined">
                            <CardContent>
                              <Grid container spacing={1}>
                                <Grid size={{ xs: 12, sm: 4 }}>
                                  <Box display="flex" flexDirection="column" justifyContent="flex-start" height="100%">
                                    <Typography variant="h5">
                                      {application.application_refno}
                                    </Typography>
                                    <Typography variant="h6" color="text.secondary">
                                      Report ID. {application.id}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {application.applicant_name.toUpperCase()}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                  <Box display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
                                    <Typography variant="caption" sx={{ backgroundColor: 'beige', px: 1, color: 'primary', fontWeight: 'bold' }} >
                                      Uploaded {formatDuration({ days: Math.floor((new Date() - new Date(application.uploaded_at)) / (1000 * 60 * 60 * 24)) })} ago • {format(new Date(application.uploaded_at), 'dd/MM/yyyy')}
                                    </Typography>
                                    <Typography variant="caption" sx={{ px: 1, color: 'text.secondary'}}>
                                      Field Assigned on {format(new Date(application.field_assigned_at), 'dd/MM/yyyy')} • {application.for_damage_type.map((type) => type).join(', ').toUpperCase()} Damage
                                    </Typography>
                                    <Divider sx={{ my: 0.5 }} />
                                    <Typography variant="caption" sx={{ px: 1, color: 'text.primary' }}>
                                      {(application.ward_name + ' • ' + application.gpu_name + ' • ' + application.subdivision_name).toUpperCase()}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 2 }} display="flex" alignItems="center" justifyContent="flex-end">
                                  <Button variant="outlined" fullWidth
                                    onClick={
                                      () => navigate(`/inspections/unverified-reports?reportId=${application.id}`)
                                    }
                                  >
                                    Check Report
                                  </Button>
                                </Grid>
                              </Grid>
                            </CardContent>
                          </Card>
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No Reports currently assigned to you for verification.
                        </Typography>
                      )
                    }
                  </Box>
                  
                  <Button 
                    variant="contained" 
                    fullWidth
                    onClick={
                      () => (navigate('/inspections/unverified-reports'))
                    }
                    sx={{ mt: 2 }}
                  >
                    View All Unverified Reports
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid size={{ xs: 12, md: 4 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary" fontWeight="bold" mb={2}>
                    Quick Actions
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Button 
                      variant="outlined"
                      fullWidth
                      onClick={() => navigate('/inspections/all-assigned-requests/head')}
                    >
                      View All Reports
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            </>
          )
        }
      </Grid>
    </Box>
  );
};

export default OfficerDashboard;
